import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import SEO from '../components/SEO';
import FaqSection from '../components/FaqSection';
import GlobeComponent from '../components/Globe';
import HomeSplash from '../components/HomeSplash';
import { getHomeFaqs, buildFaqLd } from '../lib/faqs';
import { Search, ChevronRight, Globe, Wifi, Shield, Zap, Check, Smartphone, CheckCircle, XCircle, ArrowRight, Layers } from 'lucide-react';
import axios from 'axios';
import { collapseByGroups } from '../lib/destinationGroups';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { allCountries, loadingCountries, formatPrice, destinationGroups, startingPrices } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Splash screen — shows once per browser session so the user isn't repeatedly delayed.
  const [showSplash, setShowSplash] = useState(() => {
    try {
      return !sessionStorage.getItem('connectify_splash_shown');
    } catch {
      return true;
    }
  });
  const handleSplashDone = () => {
    setShowSplash(false);
    try { sessionStorage.setItem('connectify_splash_shown', '1'); } catch { /* ignore */ }
  };
  
  // Device compatibility state
  const [compatibleDevices, setCompatibleDevices] = useState([]);
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
  const [showDeviceSuggestions, setShowDeviceSuggestions] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [showDeviceModal, setShowDeviceModal] = useState(false);

  // Track home page view
  useEffect(() => {
    let sessionId = sessionStorage.getItem('connectify_session_id');
    if (!sessionId) {
      sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      sessionStorage.setItem('connectify_session_id', sessionId);
    }
    
    axios.post(`${API}/track/page`, {
      page_type: 'home',
      session_id: sessionId
    }).catch(err => console.log('Tracking error:', err));
  }, []);

  // Fetch compatible devices on mount
  useEffect(() => {
    const fetchDevices = async () => {
      setLoadingDevices(true);
      try {
        const response = await axios.get(`${API}/compatible-devices`);
        if (response.data.success && response.data.data) {
          setCompatibleDevices(response.data.data);
          console.log('Loaded compatible devices:', response.data.data.length);
        }
      } catch (error) {
        console.error('Error fetching compatible devices:', error);
      } finally {
        setLoadingDevices(false);
      }
    };
    fetchDevices();
  }, []);

  // Group-collapsed catalog so grouped destinations show as one card. Tapping
  // a card navigates to the destination detail page as normal — the detail
  // page is responsible for opening the picker modal for group members.
  const groupedCountries = useMemo(
    () => collapseByGroups(allCountries, destinationGroups, startingPrices),
    [allCountries, destinationGroups, startingPrices],
  );
  const openOrNavigate = useCallback((country) => {
    navigate(`/destinations/${country.id}`);
  }, [navigate]);

  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return groupedCountries
      .filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
      .slice(0, 6);
  }, [searchQuery, groupedCountries]);

  // Filter devices for compatibility search - improved matching
  const filteredDevices = useMemo(() => {
    if (!deviceSearchQuery.trim() || deviceSearchQuery.length < 2) return [];
    if (compatibleDevices.length === 0) return [];
    
    const query = deviceSearchQuery.toLowerCase().trim();
    const queryWords = query.split(/\s+/);
    
    // Score-based matching for better results
    const scored = compatibleDevices.map(device => {
      const model = (device.model || '').toLowerCase();
      const name = (device.name || '').toLowerCase();
      const brand = (device.brand || '').toLowerCase();
      const combined = `${model} ${name} ${brand}`;
      
      let score = 0;
      
      // Exact match in model or name (highest priority)
      if (model.includes(query) || name.includes(query)) {
        score += 100;
      }
      
      // Brand match
      if (brand.includes(query)) {
        score += 50;
      }
      
      // All query words found somewhere
      const allWordsFound = queryWords.every(word => combined.includes(word));
      if (allWordsFound) {
        score += 30;
      }
      
      // Partial word matches
      queryWords.forEach(word => {
        if (combined.includes(word)) {
          score += 10;
        }
      });
      
      return { device, score };
    });
    
    // Filter and sort by score
    return scored
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map(item => item.device);
  }, [deviceSearchQuery, compatibleDevices]);

  // Popular destinations
  // Featured destinations from admin settings
  const [featuredIds, setFeaturedIds] = useState(null);

  useEffect(() => {
    axios.get(`${API}/featured-destinations`).then(res => {
      if (res.data.success) setFeaturedIds(res.data.destinations);
    }).catch(() => {});
  }, []);

  const popularDestinations = useMemo(() => {
    if (!featuredIds || groupedCountries.length === 0) return [];
    // Match by country code (id) — supports both countries and regions.
    // Uses the group-collapsed list so featured variants show as the group.
    const matched = [];
    for (const code of featuredIds) {
      const found = groupedCountries.find(c => c.id === code || c.code === code);
      if (found) matched.push(found);
    }
    return matched.slice(0, 8);
  }, [groupedCountries, featuredIds]);

  // Regional plans
  // Featured regional plans: Europe, Asia, Global, North America
  const regionalPlans = useMemo(() => {
    // Specific IDs for featured regional plans
    const featuredIds = ['EU', 'ASIA_REG', 'GLO', 'NAM'];
    
    // Find matching plans and maintain order
    const featured = [];
    for (const id of featuredIds) {
      const plan = allCountries.find(c => c.id === id || 
        (c.isRegion && c.id === 'ASR' && id === 'ASIA_REG') // ASR is Asia regional
      );
      if (plan) featured.push(plan);
    }
    
    // If ASIA_REG not found, try ASR (Asia)
    if (!featured.find(p => p.id === 'ASIA_REG' || p.id === 'ASR')) {
      const asiaPlan = allCountries.find(c => c.isRegion && c.name.toLowerCase() === 'asia');
      if (asiaPlan) featured.splice(1, 0, asiaPlan); // Insert at position 1
    }
    
    return featured.slice(0, 4);
  }, [allCountries]);

  const handleSearch = useCallback(() => {
    if (filteredCountries.length > 0) {
      openOrNavigate(filteredCountries[0]);
    } else if (searchQuery.trim()) {
      navigate(`/destinations?search=${encodeURIComponent(searchQuery)}`);
    }
  }, [filteredCountries, searchQuery, navigate, openOrNavigate]);

  const features = [
    { icon: "⚡", title: "Instant Activation", desc: "Get connected in minutes" },
    { icon: "🌍", title: "200+ Countries", desc: "Global coverage guaranteed" },
    { icon: "📶", title: "5G/LTE Ready", desc: "Fastest speeds available" },
    { icon: "🔒", title: "Secure & Reliable", desc: "Enterprise-grade security" }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      {showSplash && <HomeSplash onDone={handleSplashDone} />}
      <SEO 
        title="Buy Travel eSIM Online"
        description="Get instant mobile data for your next trip with Connectify eSIMs. No SIM swapping needed, instant activation, coverage in 200+ countries. Plans starting from $2.99."
        keywords="travel eSIM, international data, roaming, buy eSIM online, digital SIM card, prepaid eSIM, Europe eSIM, Asia eSIM, USA eSIM"
        canonical="/"
        breadcrumbs={[{ name: 'Home', url: '/' }]}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Connectify eSIMs',
            url: 'https://connectify-esims.com',
            logo: 'https://customer-assets.emergentagent.com/job_esim-connect-6/artifacts/3a8g7p2b_20571.webp',
            description: 'Premium eSIM provider offering instant mobile data for travelers in 200+ countries',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: 'hello@connectify-esims.com',
            },
          },
          buildFaqLd(getHomeFaqs()),
        ]}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="dark:border-t dark:border-white/15 relative bg-white dark:bg-black pt-24 pb-12 sm:pt-28 sm:pb-16">
        <div className="relative z-10 flex flex-col items-center text-center px-4">
          {/* Rotating Globe with Landmarks */}
          <div className="mb-4 sm:mb-6">
            <GlobeComponent />
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 dark:text-neutral-100 mb-4 leading-tight max-w-4xl">
            Travel Smart, Stay Connected
          </h1>

            {/* Search Bar */}
            <div className="relative w-full max-w-xl">
              <div className="flex items-center bg-gray-50 dark:bg-neutral-950 rounded-full overflow-hidden shadow-lg border border-gray-200 dark:border-2 dark:border-white">
                <div className="flex items-center flex-1 px-4 sm:px-6 min-w-0">
                  <Search className="text-gray-400 dark:text-white w-5 h-5 mr-2 sm:mr-3 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Where are you traveling?"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-full py-3 sm:py-4 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-400 focus:outline-none bg-transparent text-base sm:text-lg truncate"
                    data-testid="hero-search-input"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="bg-black text-white p-3 sm:px-8 sm:py-4 m-1.5 rounded-full font-bold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 flex-shrink-0"
                  data-testid="hero-search-btn"
                >
                  <span className="hidden sm:inline text-sm">Search</span>
                  <ArrowRight size={20} />
                </button>
              </div>

              {/* Search Suggestions */}
              {showSuggestions && filteredCountries.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-black rounded-2xl shadow-xl border border-gray-100 dark:border-neutral-800 z-50 max-h-72 overflow-y-auto" onMouseDown={(e) => e.preventDefault()} data-testid="home-search-suggestions">
                  {filteredCountries.map(country => (
                    <button
                      key={country.id}
                      onClick={() => { openOrNavigate(country); setShowSuggestions(false); }}
                      className="w-full flex items-center gap-4 px-5 py-3 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors text-left"
                    >
                      {country.isRegion ? (
                        <div className="w-8 h-6 bg-gradient-to-br from-blue-500 to-emerald-500 rounded shadow-sm flex items-center justify-center">
                          <Globe size={16} className="text-white" />
                        </div>
                      ) : (
                        <img src={country.flag} alt={`${country.name} flag`} className="w-8 h-6 object-cover rounded shadow-sm" />
                      )}
                      <p className="font-semibold text-gray-900 dark:text-neutral-100 flex-1">{country.name}</p>
                      {country._group ? (
                        <span className="text-xs font-bold text-gray-500 dark:text-neutral-400 flex items-center gap-1">
                          <Layers size={12} /> {(country._group.members || []).length}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              )}
            </div>

          <p className="text-base sm:text-lg text-gray-500 dark:text-neutral-500 mt-6 font-medium max-w-xl">
            Premium eSIM solutions for 200+ countries. No roaming charges. Instant activation.
          </p>
        </div>
      </section>

      {/* Why Choose Connectify Section */}
      <section className="dark:border-t dark:border-white/15 relative py-24 bg-gray-950 overflow-hidden">
        {/* Animated background orbs */}
        <div className="absolute top-[-120px] left-[-80px] w-[400px] h-[400px] rounded-full bg-blue-500/10 blur-[100px] animate-pulse" />
        <div className="absolute bottom-[-100px] right-[-60px] w-[350px] h-[350px] rounded-full bg-emerald-500/10 blur-[100px] animate-pulse" style={{ animationDelay: '1.5s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-purple-500/5 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-emerald-400 text-sm font-semibold tracking-widest uppercase mb-3" style={{ animation: 'fadeSlideUp 0.6s ease-out both' }}>Built for travelers</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4" style={{ animation: 'fadeSlideUp 0.6s ease-out 0.1s both' }}>Why Choose Connectify?</h2>
            <p className="text-gray-400 dark:text-neutral-500 max-w-xl mx-auto" style={{ animation: 'fadeSlideUp 0.6s ease-out 0.2s both' }}>The smartest way to stay connected while traveling</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feature, i) => (
              <div
                key={feature.title}
                className="group relative bg-white/[0.04] backdrop-blur-md rounded-2xl p-6 sm:p-8 text-center border border-white/[0.08] hover:border-white/20 hover:bg-white/[0.08] transition-all duration-500 hover:-translate-y-1"
                style={{ animation: `fadeSlideUp 0.5s ease-out ${0.15 + i * 0.1}s both` }}
                data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {/* Glow on hover */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/0 to-blue-500/0 group-hover:from-emerald-500/5 group-hover:to-blue-500/5 transition-all duration-500" />
                <div className="relative">
                  <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5 text-3xl">
                    {feature.icon}
                  </div>
                  <h3 className="font-bold text-white mb-2 text-sm sm:text-base">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-neutral-500 group-hover:text-gray-400 transition-colors">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className="dark:border-t dark:border-white/15 py-20 bg-white dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-neutral-100 mb-2">Popular Destinations</h2>
              <p className="text-gray-600 dark:text-neutral-400">Most loved by travelers worldwide</p>
            </div>
            <button 
              onClick={() => navigate('/destinations')}
              className="hidden sm:flex items-center gap-2 text-gray-900 dark:text-neutral-100 font-bold hover:underline"
              data-testid="view-all-destinations"
            >
              View All <ChevronRight size={20} />
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {loadingCountries ? (
              Array(8).fill(0).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-gray-200 dark:bg-neutral-700 rounded-2xl" />
                  <div className="mt-3 h-5 bg-gray-200 dark:bg-neutral-700 rounded w-3/4" />
                  <div className="mt-2 h-4 bg-gray-200 dark:bg-neutral-700 rounded w-1/2" />
                </div>
              ))
            ) : (
              popularDestinations.map(country => (
                <button
                  key={country.id}
                  onClick={() => openOrNavigate(country)}
                  className="group text-left"
                  data-testid={`destination-${country.id}`}
                >
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
                    <img
                      src={country.image || `https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80`}
                      alt={country.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    {country._group ? (
                      <div className="absolute top-3 right-3 bg-black/80 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                        <Layers size={12} className="text-white" />
                        <span className="text-xs font-bold text-white">{(country._group.members || []).length} plans</span>
                      </div>
                    ) : null}
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="flex items-center gap-2">
                        {country.isRegion ? (
                          <span className="text-lg leading-none">
                            {country.name.toLowerCase().includes('europe') ? '🇪🇺' :
                             country.name.toLowerCase().includes('asia') ? '🌏' :
                             country.name.toLowerCase().includes('africa') ? '🌍' :
                             country.name.toLowerCase().includes('north america') ? '🌎' : '🌐'}
                          </span>
                        ) : (
                          <img src={country.flag} alt={`${country.name} flag`} className="w-6 h-4 rounded shadow" />
                        )}
                        <span className="text-white font-bold text-sm">{country.name}</span>
                      </div>
                      {(() => {
                        const price = country._group
                          ? country._groupMinPrice
                          : startingPrices[country.id];
                        return typeof price === 'number' && price > 0 ? (
                          <p className="text-white/80 text-xs mt-1">from <span className="font-bold text-white">{formatPrice(price)}</span></p>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          <button 
            onClick={() => navigate('/destinations')}
            className="sm:hidden w-full mt-8 py-4 border-2 border-gray-900 rounded-xl font-bold text-gray-900 dark:text-neutral-100 hover:bg-gray-900 hover:text-white transition-colors"
          >
            View All Destinations
          </button>
        </div>
      </section>

      {/* Regional Plans */}
      {regionalPlans.length > 0 && (
        <section className="dark:border-t dark:border-white/15 py-20 bg-black text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black mb-4">Regional eSIM Plans</h2>
              <p className="text-gray-400 dark:text-neutral-500 max-w-2xl mx-auto">One eSIM for multiple countries. Perfect for multi-destination trips.</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {regionalPlans.map(plan => {
                const regionEmoji = plan.name.toLowerCase().includes('europe') ? '🇪🇺' :
                  plan.name.toLowerCase().includes('asia') ? '🌏' :
                  plan.name.toLowerCase().includes('north america') ? '🌎' :
                  plan.name.toLowerCase().includes('africa') ? '🌍' :
                  '🌐';
                return (
                <button
                  key={plan.id}
                  onClick={() => navigate(`/destinations/${plan.id}`)}
                  className="group bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 hover:from-gray-800 hover:to-gray-700 transition-all text-left border border-gray-700 hover:border-gray-600 shadow-lg hover:shadow-xl"
                  data-testid={`regional-${plan.id}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">{regionEmoji}</span>
                    <span className="text-white font-bold text-lg">{plan.name}</span>
                  </div>
                  <p className="text-gray-400 dark:text-neutral-500 text-sm mb-4">Coverage across {plan.name}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-emerald-400 font-semibold">Multiple Countries</span>
                    <ChevronRight className="text-white/50 group-hover:translate-x-1 group-hover:text-emerald-400 transition-all" />
                  </div>
                </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="dark:border-t dark:border-white/15 py-20 bg-gradient-to-b from-white to-gray-50 dark:from-black dark:to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-neutral-100 mb-4">How It Works</h2>
            <p className="text-gray-600 dark:text-neutral-400 max-w-2xl mx-auto text-lg">Get connected in 3 simple steps — no physical SIM needed</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { 
                step: "1", 
                title: "Choose Your Plan", 
                desc: "Select your destination and pick the perfect data package for your trip",
                icon: "🌍"
              },
              { 
                step: "2", 
                title: "Complete Purchase", 
                desc: "Secure checkout with instant eSIM activation",
                icon: "💳"
              },
              { 
                step: "3", 
                title: "One-Tap Install", 
                desc: "Install instantly on iPhone or Android with our one-tap install buttons — or scan the QR code",
                icon: "📱"
              }
            ].map((item) => (
              <div key={item.step} className="relative bg-white dark:bg-black rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-neutral-800 hover:shadow-lg transition-shadow">
                <div className="absolute -top-5 left-8 w-10 h-10 bg-black text-white rounded-full flex items-center justify-center text-lg font-black">
                  {item.step}
                </div>
                <div className="text-4xl mb-4 mt-2">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-3">{item.title}</h3>
                <p className="text-gray-600 dark:text-neutral-400">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center space-y-4">
            <p className="text-sm text-gray-500 dark:text-neutral-500 bg-gray-100 dark:bg-neutral-800 inline-block px-6 py-3 rounded-full">
              <span className="font-semibold">Pro tip:</span> One-tap install works on iOS 17.4+ and most Android devices
            </p>
            <div>
              <button
                onClick={() => setShowDeviceModal(true)}
                className="px-6 py-3 bg-black text-white rounded-full font-bold hover:bg-gray-800 transition-colors text-sm"
                data-testid="open-device-checker"
              >
                Is my device compatible?
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Device Compatibility Modal */}
      {showDeviceModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="device-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeviceModal(false)} />
          <div className="relative bg-white dark:bg-black rounded-3xl p-6 sm:p-10 max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl">
            <button
              onClick={() => setShowDeviceModal(false)}
              className="absolute top-4 right-4 text-gray-400 dark:text-neutral-500 hover:text-gray-600"
              data-testid="close-device-modal"
            >
              <XCircle size={24} />
            </button>
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-100 dark:bg-emerald-500/15 rounded-2xl mb-4">
                <Smartphone className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-neutral-100 mb-2">Is Your Device Compatible?</h2>
              <p className="text-gray-500 dark:text-neutral-500 text-sm">Check if your device supports eSIM technology</p>
            </div>

            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-neutral-500 pointer-events-none" size={20} />
              <input
                type="text"
                placeholder="Search your device (e.g., iPhone 15)"
                value={deviceSearchQuery}
                onChange={(e) => {
                  setDeviceSearchQuery(e.target.value);
                  setShowDeviceSuggestions(true);
                  if (e.target.value !== selectedDevice?.model && e.target.value !== selectedDevice?.name) {
                    setSelectedDevice(null);
                  }
                }}
                onFocus={() => setShowDeviceSuggestions(true)}
                onBlur={() => setTimeout(() => setShowDeviceSuggestions(false), 300)}
                className="w-full pl-12 pr-5 py-4 rounded-xl border-2 border-gray-200 dark:border-neutral-800 focus:border-emerald-500 focus:outline-none text-base transition-colors"
                data-testid="device-search-input"
              />
              {loadingDevices && (
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>

            {/* Dropdown Suggestions */}
            {showDeviceSuggestions && deviceSearchQuery.length >= 2 && !loadingDevices && (
              <div className="mt-2 bg-white dark:bg-black rounded-xl shadow-lg border border-gray-100 dark:border-neutral-800 overflow-hidden max-h-60 overflow-y-auto">
                {filteredDevices.length > 0 ? (
                  filteredDevices.map((device) => (
                    <button
                      key={device.model || device.name}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSelectedDevice(device);
                        setDeviceSearchQuery(device.model || device.name);
                        setShowDeviceSuggestions(false);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-emerald-50 flex items-center gap-3 border-b border-gray-50 last:border-0 transition-colors"
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm ${
                        device.os === 'ios' ? 'bg-gray-900 text-white' : 
                        device.os === 'android' ? 'bg-green-500 text-white' : 
                        'bg-blue-500 text-white'
                      }`}>
                        {device.os === 'ios' ? '🍎' : device.os === 'android' ? '🤖' : '💻'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-neutral-100 text-sm truncate">{device.model || device.name}</p>
                        <p className="text-xs text-gray-500 dark:text-neutral-500">{device.brand} • {device.deviceType}</p>
                      </div>
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-6 text-center">
                    <XCircle className="w-10 h-10 text-amber-400 mx-auto mb-2" />
                    <p className="font-semibold text-gray-900 dark:text-neutral-100 text-sm">No matching devices found</p>
                    <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">Your device might not be eSIM compatible</p>
                  </div>
                )}
              </div>
            )}

            {/* Result */}
            {selectedDevice && (
              <div className="mt-6 text-center">
                <div className="inline-flex items-center gap-2 bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 px-5 py-3 rounded-xl">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-bold">Your device is eSIM ready!</span>
                </div>
                <p className="text-gray-600 dark:text-neutral-400 mt-3 text-sm">
                  <span className="font-semibold">{selectedDevice.model || selectedDevice.name}</span> supports eSIM
                </p>
                <button
                  onClick={() => { setShowDeviceModal(false); navigate('/destinations'); }}
                  className="mt-4 px-6 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors text-sm"
                >
                  Browse eSIM Plans →
                </button>
              </div>
            )}

            {/* Popular devices */}
            {!deviceSearchQuery && !selectedDevice && (
              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500 dark:text-neutral-500 mb-3">Popular eSIM devices:</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['iPhone 15', 'iPhone 14', 'Galaxy S24', 'Pixel 8', 'iPad Pro'].map((device) => (
                    <button
                      key={device}
                      onClick={() => {
                        setDeviceSearchQuery(device);
                        setShowDeviceSuggestions(true);
                      }}
                      className="px-3 py-1.5 bg-gray-100 dark:bg-neutral-800 rounded-full text-xs text-gray-700 dark:text-neutral-300 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                    >
                      {device}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <FaqSection faqs={getHomeFaqs()} testIdPrefix="home-faq" />

      <Footer />
    </div>
  );
};

export default Home;
